import * as React from 'react';

import {
  Button,
  CircularProgress,
  InputBase,
  Menu,
  MenuItem,
} from '@mui/material';
import { Compass, Search } from 'lucide-react';

import type { ModelInfo } from '@modules/_shared/components/chat/useModels';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { isRealAdvisorAdmin } from '@modules/_shared/utils/access';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

type Props = {
  models: ModelInfo[];
  selectedModelId: string | null;
  onModelChange: (modelId: string) => void;
  isLoading: boolean;
};

const AnthropicIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    css={{ flexShrink: 0 }}
  >
    <path d="M17.308 3H13.69L20 21h3.615L17.308 3ZM7.385 3 1 21h3.692l1.308-3.692h6.923L14.23 21h3.693L11.538 3H7.385Zm-.462 11.077L10 6.462l3.077 7.615H6.923Z" />
  </svg>
);

const OpenAIIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    css={{ flexShrink: 0 }}
  >
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073ZM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494ZM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646ZM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872v.024Zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667Zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66v.018Zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681l-.004 6.722Zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5-.005-2.999Z" />
  </svg>
);

const GoogleIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" css={{ flexShrink: 0 }}>
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84Z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z"
      fill="#EA4335"
    />
  </svg>
);

const XAIIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    css={{ flexShrink: 0 }}
  >
    <path d="M1.19 8.48L12.05 24L16.88 24L6.02 8.48ZM1.18 24L6.01 24L8.43 20.55L6.01 17.1ZM22.82 0L17.99 0L9.64 11.93L12.05 15.38ZM18.86 24L22.82 24L22.82 1.73L18.86 7.38Z" />
  </svg>
);

const ProviderIcon = ({
  provider,
  size = 16,
}: {
  provider: string;
  size?: number;
}) => {
  switch (provider) {
    case 'anthropic':
      return <AnthropicIcon size={size} />;
    case 'openai':
      return <OpenAIIcon size={size} />;
    case 'google':
      return <GoogleIcon size={size} />;
    case 'xai':
      return <XAIIcon size={size} />;
    default:
      return null;
  }
};

export const ModelSelector = ({
  models,
  selectedModelId,
  onModelChange,
  isLoading,
}: Props) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const { me } = useAppData();
  const canPickModel = isRealAdvisorAdmin(me);
  // A focused search field opens the software keyboard and covers the list.
  const coarsePointer =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: coarse)').matches;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [query, setQuery] = React.useState('');

  const open = Boolean(anchorEl);

  const filteredModels = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return models;
    }
    return models.filter(
      model =>
        model.name.toLowerCase().includes(needle) ||
        model.provider.toLowerCase().includes(needle) ||
        model.id.toLowerCase().includes(needle),
    );
  }, [models, query]);

  const handleClose = () => {
    setAnchorEl(null);
    setQuery('');
  };

  const openMenu = (target: HTMLElement) => {
    if (!canPickModel) {
      return;
    }
    setAnchorEl(target);
  };

  // Desktop left-click stays inert so the label does not read as a dropdown.
  // iOS Safari never fires contextmenu on long-press, and Enter/Space only
  // synthesize a click (detail === 0), so those paths still open the menu.
  // Only RealAdvisor admins (is_admin and a @realadvisor.com email) can open it.
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canPickModel) {
      event.preventDefault();
      return;
    }
    const fromKeyboard = event.detail === 0;
    if (fromKeyboard || coarsePointer) {
      openMenu(event.currentTarget);
      return;
    }
    event.preventDefault();
  };

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  return (
    <>
      <Button
        size="small"
        variant="text"
        type="button"
        disableRipple
        aria-haspopup={canPickModel ? 'menu' : undefined}
        aria-expanded={canPickModel ? open : undefined}
        onClick={handleClick}
        onContextMenu={e => {
          if (!canPickModel) {
            return;
          }
          e.preventDefault();
          openMenu(e.currentTarget);
        }}
        sx={{
          color: colors.grey700,
          // Plain label. RealAdvisor admins open the menu by right-click, touch, or keyboard.
          textTransform: 'none',
          fontWeight: 600,
          lineHeight: 1,
          alignItems: 'center',
          cursor: 'default',
          backgroundColor: 'transparent',
          '&:hover, &:focus, &:active, &.Mui-focusVisible': {
            backgroundColor: 'transparent',
            color: colors.grey700,
          },
        }}
      >
        <span css={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Compass size={16} css={{ display: 'block', flexShrink: 0 }} />
          <span css={{ lineHeight: '16px' }}>{t('Aura AI')}</span>
        </span>
      </Button>
      {canPickModel && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          aria-label={t('Select model')}
          autoFocus={false}
          disableAutoFocusItem
          slotProps={{
            paper: {
              sx: { maxHeight: 400, minWidth: 260 },
            },
            list: {
              sx: { pt: 0 },
            },
          }}
        >
          <div
            css={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              background: colors.white,
              padding: '8px 8px 6px',
              borderBottom: `1px solid ${colors.grey200}`,
            }}
          >
            <InputBase
              autoFocus={!coarsePointer}
              fullWidth
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('Search')}
              startAdornment={
                <Search
                  size={14}
                  css={{ marginRight: 6, color: colors.grey500, flexShrink: 0 }}
                />
              }
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  handleClose();
                  return;
                }
                // Let the MenuList own navigation/selection (arrows, Enter, Home,
                // End, Tab) so the picker stays keyboard-operable; stop everything
                // else so MUI's type-ahead doesn't steal the typed characters.
                if (
                  e.key !== 'ArrowDown' &&
                  e.key !== 'ArrowUp' &&
                  e.key !== 'Home' &&
                  e.key !== 'End' &&
                  e.key !== 'Enter' &&
                  e.key !== 'Tab'
                ) {
                  e.stopPropagation();
                }
              }}
              sx={{
                fontSize: 13,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                background: colors.grey100,
                border: `1px solid transparent`,
                transition: 'border-color 120ms, background 120ms',
                '&.Mui-focused': {
                  background: colors.white,
                  borderColor: colors.grey300,
                },
                '& .MuiInputBase-input': {
                  p: 0,
                  '&::placeholder': { color: colors.grey400, opacity: 1 },
                },
              }}
            />
          </div>
          {filteredModels.length === 0 ? (
            <MenuItem disabled>{t('No results')}</MenuItem>
          ) : (
            filteredModels.map(model => (
              <MenuItem
                key={model.id}
                disableRipple
                selected={model.id === selectedModelId}
                onClick={() => {
                  onModelChange(model.id);
                  handleClose();
                }}
                sx={{ gap: 1 }}
              >
                <ProviderIcon provider={model.provider} />
                {model.name}
              </MenuItem>
            ))
          )}
        </Menu>
      )}
    </>
  );
};
